import { ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { VngDialogRef, VNG_DIALOG_DATA } from "@vengreso/angular-components";
import { FormGroup, FormBuilder } from "@angular/forms";
import { fireConfetti } from "src/app/utils/confetti.util";

import { DataService } from 'src/app/core/core-services/services/dataservices/data.service';
import { NotificationService } from 'src/app/core/core-services/services/notification.service';
import { ValidationConstants } from "src/app/core/core-services/constants/validation.constants";

import { BASE_FLYSHARE_LINK } from "./fly-cut.config";
import { AuthService } from 'src/app/core/core-services/services/auth.service';
import { DialogService } from 'src/app/core/core-services/services/dialog.service';
import { ProfileUpdatedSuccessfullyComponent } from 'src/app/components/dialogs/profile-updated-successfully/profile-updated-successfully.component';
import { Store } from '@ngrx/store';
import { selectPlanData } from 'src/app/core/store/plan/plan.selectors';

@Component({
    selector: 'app-fly-cut-share',
    templateUrl: './fly-cut-share.component.html',
    styleUrls: ['./fly-cut-share.component.scss'],
    standalone: false
})
export class FlyCutShareComponent implements OnInit {
  
  flycutIds: string[];
  flyshareInviteLink: string;
  flyshareToken: string;
  fromFlyPlates: boolean;
  shortcutsCloned: boolean = false;
  showFlyMsgShared: boolean = false;
  inviteCounter = 0;
  firstPersonSignedUp: any = false;
  secondPersonSignedUp: any = false;
  thirdPersonSignedUp: any = false;
  fourthPersonSignedUp: any = false;

  firstName:string;
  lastName:string;
  authEmail: string;
  nameMaxLength:number = 12;

  form: FormGroup;
  emails: string[] = [];

  timeout: any = null;

  @ViewChild('emailInput', { static: false }) emailInput!: ElementRef<HTMLInputElement>;

  // selectPlanData, not `state => state.data`: the root state is { plan: PlanState },
  // so `state.data` is always undefined and the old destructuring subscriber threw.
  currentPlan$ = this.store.select(selectPlanData);

  constructor(
    @Inject(VNG_DIALOG_DATA) public data : { shortcuts: any, fromFlyPlates: boolean},
    public dialog: DialogService,
    public dialogRef: VngDialogRef<FlyCutShareComponent>,
    private dataService: DataService,
    private notification: NotificationService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    public authService: AuthService,
    private store: Store,
  ) {

    this.fromFlyPlates = data.fromFlyPlates;

    this.flycutIds = data.shortcuts.map(({id, reference_shortcut_id}) => reference_shortcut_id || id);

    const { first_name, last_name, email } = this.authService.userDetails.user_details;

    this.firstName = first_name;
    this.lastName = last_name;
    this.authEmail = email;

    this.form = this.fb.group({
      first_name: [
        first_name,
        [
          Validators.required,
          Validators.pattern(ValidationConstants.NON_DIGIT),
          Validators.maxLength(12)
        ],
      ],
      last_name: [
        last_name,
        [
          Validators.required,
          Validators.pattern(ValidationConstants.NON_DIGIT),
          Validators.maxLength(12)
        ],
      ],
      message: ['']
    });
  }

  ngOnInit(): void {
    this.currentPlan$
     .subscribe( (currentPlan) => {
          if (currentPlan?.referrals) {
            const referrals = currentPlan.referrals;

            if (referrals[0]) {
              this.inviteCounter = 1
              this.firstPersonSignedUp = JSON.parse(referrals[0])
            }
            if (referrals[1]) {
              this.inviteCounter = 2
              this.secondPersonSignedUp = JSON.parse(referrals[1])
            }
            if (referrals[2]) {
              this.inviteCounter = 3
              this.thirdPersonSignedUp = JSON.parse(referrals[2])
            }
            if (referrals[3]) {
              this.inviteCounter = 4
              this.fourthPersonSignedUp = JSON.parse(referrals[3])
            }
            this.cdr.detectChanges();
          }
        }
     );
    if (!this.flyshareInviteLink && !this.fromFlyPlates) {
      this.retrieveFlyshareToken();
    }
    // R3: markAllAsTouched() used to run here, which made the dialog open with
    // the name fields already painted red whenever the profile-prefilled value
    // failed validation. It moved to share() (the submit path) instead.
  }

  // Replaces the old matChipInputTokenEnd handler (04 §3.2 composition): reads
  // the native input's current value directly instead of a MatChipInputEvent.
  addFromInput(): void {
    const value = (this.emailInput.nativeElement.value || '').trim();

    if (value) {
      this.emails.push(value);
    }

    // Clear the input value
    this.emailInput.nativeElement.value = '';
  }

  // Splits pasted text on comma/space/newline so multiple emails paste in as
  // separate chips (04 §3.2 composition). A single-value paste (no separator
  // found) is left alone so the browser's default paste still fills the input.
  onPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text');

    if (!pastedText) {
      return;
    }

    const parts = pastedText
      .split('\n')
      .join(',')
      .split(' ')
      .join(',')
      .split(',')
      .map((email) => email.trim())
      .filter((email) => !!email);

    if (parts.length > 1) {
      event.preventDefault();
      parts.forEach((email) => this.emails.push(email));
      this.emailInput.nativeElement.value = '';
    }
  }

  onKeyUp(event: any) {
    clearTimeout(this.timeout);
    let that = this;

    this.timeout = setTimeout(function () {
      if (event.key !== 'Enter') {
        const email = (event.target.value || '').trim();

        const emailPatternValidator = new FormControl(email.toLowerCase(), [
          Validators.pattern(ValidationConstants.EMAIL_EXTRA_REGEX),
          Validators.minLength(5),
          Validators.email,
          Validators.required
        ]);

        if(emailPatternValidator.valid) {
          event.target.value = ''
          that.emails.push(email)
        }
      }
    }, 1000);
  }

  remove(email: any): void {
    const i = this.emails.indexOf(email);

    if (i >= 0) {
      this.emails.splice(i, 1);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  retrieveFlyshareToken(): void {
    this.dataService.post({url: 'v1/flyshares', data: {shortcut_ids : this.flycutIds}, isLoader: false}).subscribe({
      next: (response: any) => {
        this.flyshareToken = response.result?.token;
        this.setFlyCutLink();
      },
      error: () => {
        this.notification.toast('Link Generation error', 'Ok', 2000)
      }
    });
  }

  setFlyCutLink() {
    const { value : {first_name, last_name} } = this.form;

    this.flyshareInviteLink = `${BASE_FLYSHARE_LINK}/${this.flyshareToken}?f=${first_name}&l=${last_name}`
  }

  // The shared app-copy-link-field owns the clipboard copy + tooltip; this
  // host-side (copied) handler keeps the one-time clone POST.
  onLinkCopied(): void {
    if (!this.shortcutsCloned) {
      this.dataService.post({url: `v1/flyshares/clone/${this.flyshareToken}`, isLoader: false}).subscribe({
        next: () => {
          this.shortcutsCloned = true;
        },
        error: () => {
        }
      });
    }
  }

  share(): void {
    this.form.markAllAsTouched();

    if(this.invalidEmail()){
      this.notification.toast('Invalid email(s)', 'Ok', 2000);
      return;
    }

    if (!this.form.valid) {
      this.notification.toast('Invalid Form details', 'Ok', 2000);
      return;
    }

    if (this.emails.some(email => this.authEmail === email)) {
      this.notification.toast("You can't share a flymsg with yourself", 'Ok', 2000)
      return;
    }

    const { value : {first_name, last_name, message} } = this.form;

    const data = {
      first_name,
      last_name,
      emails: this.emails.map(email => email.trim()),
      message: message
    }

    this.showFlyMsgShared = true;
    setTimeout(() => this.showFlyMsgShared = false, 3000);
    
    this.dataService.post({url: 'v1/flyshares/'+this.flyshareToken, data: data, isLoader: true}).subscribe({
      next: () => {
        this.notification.toast('FlyMSG Shared!', 'Ok', 2000);
        this.useConfettiEffect();

        if(this.nameChanged(data)) {
          this.promptUpdateProfile();
        }

        // this.form.reset();
        this.emails = [];
        this.form.controls['message'].reset()
      },
      error: (error) => {
        this.notification.toast(error.error.error ?? 'Something Went Wrong. Check fields.', 'Ok', 3000)
      }
    });
  }

  invalidEmail() {
    return this.emails.some(email => 
       (new FormControl(email.toLowerCase(), [
        Validators.pattern(ValidationConstants.EMAIL_EXTRA_REGEX),
        Validators.minLength(5),
        Validators.email,
        Validators.required
      ])).invalid
    );
  }

  nameChanged(payload: any): boolean {
    return (this.firstName !== payload.first_name || this.lastName !== payload.last_name);
  }

  promptUpdateProfile(): void {
    this.dialog
      .openDialogComponent<boolean>(ProfileUpdatedSuccessfullyComponent, { formValue: this.form.value }, "450px")
      .afterClosed()
      .subscribe((confirmed) => confirmed && this.updateProfile());
  }

  useConfettiEffect(): void {
    fireConfetti("flycut-share", { height: "95vh", bottom: "5vh", particleCount: 270, spread: 95, originY: 1 });
  }

  updateProfile() {
    let data = this.form.value
    this.dataService
      .put({ url: "v1/user", isLoader: false, data })
      .subscribe((response: any) => {
        if (response && response.result) {
          this.authService.updateUserDetail();
        }
      },
      (error) => {},
      () => {});
  }

  multiSelect(): boolean {
    return this.data.shortcuts ? this.data.shortcuts.length > 1 : false
  }
}
